import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import {
  createCompanyOauthApp,
  deleteCompanyOauthApp,
  fetchCompanyOauthApps,
  rollCompanyOauthAppKeys,
} from "../../../../api/companyOauthApps";
import { PlusIcon, KeyIcon } from "@heroicons/react/24/outline";

const EMPTY = { name: "", redirect_uri: "", scopes: "read", confidential: true };

function OauthAppRow({ app, onRoll, onDeleted }) {
  const { t } = useTranslation();
  const [confirmName, setConfirmName] = useState("");

  const handleDelete = () => {
    if (confirmName !== app.name) {
      toast.error(t("company.oauth_apps.delete_name_mismatch"));
      return;
    }
    deleteCompanyOauthApp(app.id, app.name)
      .then(() => {
        toast.success(t("company.oauth_apps.deleted"));
        setConfirmName("");
        onDeleted();
      })
      .catch((err) => toast.error(err?.response?.data?.message || t("company.oauth_apps.delete_failed")));
  };

  return (
    <li className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p className="font-semibold text-slate-950">
          {app.name}
          {app.deleted ? (
            <span className="ml-2 text-xs font-medium text-rose-600">{t("company.oauth_apps.deleted_badge")}</span>
          ) : null}
        </p>
        <p className="font-mono text-xs text-slate-500">{app.uid}</p>
        <p className="text-xs text-slate-500">
          {t("company.oauth_apps.scopes")}: {app.scopes} · {app.active_tokens_count} tokens
        </p>
      </div>
      {!app.deleted && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onRoll(app)}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold"
          >
            <KeyIcon className="h-4 w-4" />
            {t("company.oauth_apps.roll")}
          </button>
          <input
            className="rounded-lg border border-slate-200 px-2 py-1 text-xs"
            placeholder={t("company.oauth_apps.confirm_name")}
            aria-label={`${t("company.oauth_apps.confirm_name")}: ${app.name}`}
            autoComplete="off"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
          />
          <button
            type="button"
            onClick={handleDelete}
            aria-label={`${t("company.oauth_apps.delete")}: ${app.name}`}
            className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white"
          >
            {t("company.oauth_apps.delete")}
          </button>
        </div>
      )}
    </li>
  );
}

export default function OauthAppsTab() {
  const { t } = useTranslation();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState(null);

  const load = () => {
    setLoading(true);
    fetchCompanyOauthApps()
      .then((res) => setApps(res.data || []))
      .catch(() => toast.error(t("company.oauth_apps.load_failed")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = (e) => {
    e.preventDefault();
    setCreating(true);
    createCompanyOauthApp(form)
      .then((res) => {
        setRevealed({
          uid: res.data.created_uid || res.data.uid,
          secret: res.data.created_secret,
          name: res.data.name,
        });
        setForm(EMPTY);
        toast.success(t("company.oauth_apps.created"));
        load();
      })
      .catch((err) => toast.error(err?.response?.data?.message || t("company.oauth_apps.create_failed")))
      .finally(() => setCreating(false));
  };

  const handleRoll = (app) => {
    rollCompanyOauthAppKeys(app.id)
      .then((res) => {
        setRevealed({
          uid: res.data.rolled_uid || res.data.uid,
          secret: res.data.rolled_secret,
          name: res.data.name,
        });
        toast.success(t("company.oauth_apps.rolled"));
        load();
      })
      .catch((err) => toast.error(err?.response?.data?.message || t("company.oauth_apps.roll_failed")));
  };

  const copy = (value) => {
    navigator.clipboard.writeText(value);
    toast.success(t("company.integrations.copied"));
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-600">{t("company.oauth_apps.intro")}</p>

      {revealed && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">{t("company.oauth_apps.secret_once")}</p>
          <p className="mt-2 font-mono text-xs break-all">
            uid: {revealed.uid}{" "}
            <button type="button" className="text-[#1463C7]" onClick={() => copy(revealed.uid)}>
              {t("company.integrations.copy")}
            </button>
          </p>
          {revealed.secret && (
            <p className="mt-1 font-mono text-xs break-all">
              secret: {revealed.secret}{" "}
              <button type="button" className="text-[#1463C7]" onClick={() => copy(revealed.secret)}>
                {t("company.integrations.copy")}
              </button>
            </p>
          )}
        </div>
      )}

      <form onSubmit={handleCreate} className="rounded-[18px] border border-slate-200 bg-white p-5">
        <h3 className="text-base font-extrabold text-slate-950">{t("company.oauth_apps.new")}</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className="text-sm font-semibold text-slate-700">
            {t("company.oauth_apps.name")}
            <input
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </label>
          <label className="text-sm font-semibold text-slate-700">
            {t("company.oauth_apps.redirect")}
            <input
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
              value={form.redirect_uri}
              onChange={(e) => setForm({ ...form, redirect_uri: e.target.value })}
            />
          </label>
          <label className="text-sm font-semibold text-slate-700">
            {t("company.oauth_apps.scopes")}
            <input
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
              value={form.scopes}
              onChange={(e) => setForm({ ...form, scopes: e.target.value })}
            />
          </label>
        </div>
        <button
          type="submit"
          disabled={creating}
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white"
        >
          <PlusIcon className="h-4 w-4" />
          {t("company.oauth_apps.create")}
        </button>
      </form>

      <div className="overflow-hidden rounded-[18px] border border-slate-200 bg-white">
        {loading ? (
          <p className="p-5 text-sm text-slate-500">{t("Loading")}</p>
        ) : apps.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">{t("company.oauth_apps.empty")}</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {apps.map((app) => (
              <OauthAppRow key={app.id} app={app} onRoll={handleRoll} onDeleted={load} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
